import React, { useMemo, useState } from "react";
import { Pressable, Text, View } from "react-native";

import { useAIExhibitFixedNarration } from "../audio/useAIExhibitFixedNarration";
import { AI_EXHIBIT_COLORS as C } from "../aiExhibitTheme";

const SINGLE_NARRATION_BY_ID = Object.freeze({
  algorithms: "history_drivers_missing_algorithms",
  data: "history_drivers_missing_data",
  compute: "history_drivers_missing_compute",
  investment: "history_drivers_missing_investment",
});

const COMBINATION_NARRATION_BY_KEY = Object.freeze({
  "compute,data": "history_drivers_missing_compute_data",
  "algorithms,compute": "history_drivers_missing_algorithms_compute",
  "algorithms,data": "history_drivers_missing_algorithms_data",
});

function narrationIdForDriverState(enabledIds, ingredients) {
  const disabledIds = ingredients
    .map((item) => item.id)
    .filter((id) => !enabledIds.includes(id));
  const disabledKey = COMBINATION_KEY(disabledIds);

  if (!disabledIds.length) {
    return "history_drivers_all_available";
  }

  if (disabledIds.length === 1) {
    return SINGLE_NARRATION_BY_ID[disabledIds[0]];
  }

  if (enabledIds.length === 1 && enabledIds[0] === "investment") {
    return "history_drivers_investment_only_combination";
  }

  return (
    COMBINATION_NARRATION_BY_KEY[disabledKey] ||
    "history_drivers_multiple_constraints"
  );
}
import { getAIExhibit21Copy } from "../content/aiExhibit21Enhancements";

const COMBINATION_KEY = (ids) => [...ids].sort().join(",");

export default function HistoryDriversScreen({
  copy,
  language = "en",
  audioEnabled = false,
}) {
  const { playNarration } = useAIExhibitFixedNarration({
    audioEnabled,
    language,
  });
  const t21 = getAIExhibit21Copy(language).historyDrivers;
  const ingredients = copy.historyDrivers.ingredients;
  const [enabledIds, setEnabledIds] = useState(() =>
    ingredients.map((item) => item.id)
  );

  const disabledIds = useMemo(
    () => ingredients.map((item) => item.id).filter((id) => !enabledIds.includes(id)),
    [enabledIds, ingredients]
  );

  const explanation = useMemo(() => {
    if (!disabledIds.length) {
      return {
        title: t21.allTitle,
        example: t21.allExample,
        anchor: null,
      };
    }

    if (disabledIds.length === 1) {
      return t21.single[disabledIds[0]];
    }

    if (enabledIds.length === 1 && enabledIds[0] === "investment") {
      return { ...t21.combinations.investment, anchor: null };
    }

    const exact = t21.combinations[COMBINATION_KEY(disabledIds)];
    if (exact) return { ...exact, anchor: null };

    return {
      title: copy.historyDrivers.missingMany,
      example: t21.multiConstraintExample,
      anchor: null,
    };
  }, [disabledIds, enabledIds, copy.historyDrivers.missingMany, language, t21]);

  const toggleIngredient = (id) => {
    const nextEnabledIds = enabledIds.includes(id)
      ? enabledIds.filter((itemId) => itemId !== id)
      : [...enabledIds, id];

    setEnabledIds(nextEnabledIds);
    playNarration(narrationIdForDriverState(nextEnabledIds, ingredients));
  };

  const resetIngredients = () => {
    const nextEnabledIds = ingredients.map((item) => item.id);
    setEnabledIds(nextEnabledIds);
    playNarration("history_drivers_all_available");
  };

  return (
    <View style={{ padding: 28 }}>
      <Text
        style={{
          color: C.accent,
          fontSize: 13,
          fontWeight: "900",
          letterSpacing: 1.2,
        }}
      >
        {copy.historyDrivers.eyebrow}
      </Text>

      <Text
        style={{
          color: C.text,
          fontSize: 44,
          lineHeight: 50,
          fontWeight: "900",
          marginTop: 8,
        }}
      >
        {copy.historyDrivers.title}
      </Text>

      <Text
        style={{
          color: C.muted,
          fontSize: 19,
          lineHeight: 28,
          marginTop: 8,
          maxWidth: 950,
        }}
      >
        {copy.historyDrivers.subtitle}
      </Text>

      <View
        style={{
          flexDirection: "row",
          flexWrap: "wrap",
          gap: 14,
          marginTop: 26,
        }}
      >
        {ingredients.map((item) => {
          const active = enabledIds.includes(item.id);

          return (
            <Pressable
              key={item.id}
              onPress={() => toggleIngredient(item.id)}
              accessibilityRole="button"
              style={({ pressed }) => ({
                width: "23.5%",
                minWidth: 235,
                flexGrow: 1,
                minHeight: 180,
                padding: 20,
                borderRadius: 18,
                borderWidth: active ? 2 : 1,
                borderColor: active ? C.accent : C.warning,
                backgroundColor:
                  active && pressed
                    ? C.surface
                    : active
                    ? C.accentSoft
                    : C.surfaceRaised,
                opacity: active ? 1 : 0.62,
              })}
            >
              <Text style={{ fontSize: 34 }}>{item.icon}</Text>

              <Text
                style={{
                  color: C.text,
                  fontSize: 22,
                  fontWeight: "900",
                  marginTop: 12,
                }}
              >
                {item.title}
              </Text>

              <Text
                style={{
                  color: C.muted,
                  fontSize: 15,
                  lineHeight: 22,
                  marginTop: 7,
                }}
              >
                {item.text}
              </Text>

              <Text
                style={{
                  color: active ? C.success : C.warning,
                  fontSize: 12,
                  fontWeight: "900",
                  marginTop: 12,
                }}
              >
                {active ? `✓ ${t21.statusAvailable}` : `✕ ${t21.statusConstrained}`}
              </Text>
            </Pressable>
          );
        })}
      </View>

      <View
        style={{
          marginTop: 22,
          padding: 22,
          borderRadius: 18,
          borderWidth: 1,
          borderLeftWidth: 6,
          borderColor: C.border,
          borderLeftColor: disabledIds.length ? C.warning : C.accent,
          backgroundColor: C.surfaceRaised,
        }}
      >
        <Text
          style={{
            color: disabledIds.length ? C.warning : C.accent,
            fontSize: 13,
            fontWeight: "900",
            letterSpacing: 0.8,
          }}
        >
          {disabledIds.length
            ? t21.changedWorldEyebrow
            : t21.historicalExampleEyebrow}
        </Text>

        <Text
          style={{
            color: C.text,
            fontSize: 24,
            lineHeight: 31,
            fontWeight: "900",
            marginTop: 9,
          }}
        >
          {explanation.title}
        </Text>

        <Text
          style={{
            color: C.text,
            fontSize: 17,
            lineHeight: 27,
            marginTop: 12,
          }}
        >
          {explanation.example}
        </Text>

        {explanation.anchor ? (
          <View
            style={{
              marginTop: 14,
              padding: 14,
              borderRadius: 13,
              backgroundColor: C.accentSoft,
            }}
          >
            <Text style={{ color: C.muted, fontSize: 14, lineHeight: 22 }}>
              {explanation.anchor}
            </Text>
          </View>
        ) : null}
      </View>

      <Pressable
        onPress={resetIngredients}
        style={({ pressed }) => ({
          marginTop: 16,
          minHeight: 52,
          alignSelf: "flex-start",
          paddingHorizontal: 18,
          paddingVertical: 12,
          borderRadius: 14,
          borderWidth: 1,
          borderColor: C.border,
          backgroundColor: pressed ? C.accentSoft : C.surfaceRaised,
        })}
      >
        <Text style={{ color: C.text, fontSize: 16, fontWeight: "900" }}>
          ↻ {copy.historyDrivers.reset}
        </Text>
      </Pressable>
    </View>
  );
}
